import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { UserType } from './useUserVariants';

export type WidgetSize = 'SMALL' | 'MEDIUM' | 'LARGE' | 'FULL';

export interface WidgetDefinition {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string;
  component: string;
  defaultSize: WidgetSize;
  allowedUserTypes: UserType[];
  settingsSchema: Record<string, unknown> | null;
  createdAt: string;
}

export interface WidgetConfig {
  widgetId: string;
  position: {
    row: number;
    col: number;
    rowSpan?: number;
    colSpan?: number;
  };
  size?: WidgetSize;
  settings?: Record<string, unknown>;
}

export interface DashboardWidgetConfig {
  widgets: WidgetConfig[];
  gridColumns: number;
}

export function useAvailableWidgets() {
  return useQuery({
    queryKey: ['widgets', 'available'],
    queryFn: async () => {
      const response = await api.get<WidgetDefinition[]>('/widgets/available');
      return response.data;
    },
  });
}

export function useWidgetsForUserType(userType: UserType) {
  return useQuery({
    queryKey: ['widgets', 'available', userType],
    queryFn: async () => {
      const response = await api.get<WidgetDefinition[]>(`/widgets/available/${userType}`);
      return response.data;
    },
    enabled: !!userType,
  });
}

export function useWidgetByCode(code: string) {
  return useQuery({
    queryKey: ['widget', code],
    queryFn: async () => {
      const response = await api.get<WidgetDefinition>(`/widgets/${code}`);
      return response.data;
    },
    enabled: !!code,
  });
}

export function useDefaultWidgets(userType: UserType) {
  return useQuery({
    queryKey: ['widgets', 'defaults', userType],
    queryFn: async () => {
      const response = await api.get<WidgetConfig[]>(`/widgets/defaults/${userType}`);
      return response.data;
    },
    enabled: !!userType,
  });
}

export function useWidgetConfig(tenantId: string, userType: UserType) {
  return useQuery({
    queryKey: ['widget-config', tenantId, userType],
    queryFn: async () => {
      const response = await api.get<DashboardWidgetConfig>(
        `/themes/${tenantId}/widgets/${userType}`
      );
      return response.data;
    },
    enabled: !!tenantId && !!userType,
  });
}

export function useSaveWidgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      userType,
      config,
    }: {
      tenantId: string;
      userType: UserType;
      config: WidgetConfig[];
    }) => {
      const response = await api.put<DashboardWidgetConfig>(
        `/themes/${tenantId}/widgets/${userType}`,
        config
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['widget-config', tenantId, userType] });
    },
  });
}

export function useResetWidgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, userType }: { tenantId: string; userType: UserType }) => {
      const response = await api.post<DashboardWidgetConfig>(
        `/themes/${tenantId}/widgets/${userType}/reset`
      );
      return response.data;
    },
    onSuccess: (_, { tenantId, userType }) => {
      queryClient.invalidateQueries({ queryKey: ['widget-config', tenantId, userType] });
    },
  });
}
